import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Ban, IndianRupee, Plus, Wand2 } from "lucide-react";
import { api, errMsg, inr } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";

const MODES = ["cash", "bank_transfer", "upi", "cheque", "card", "neft", "rtgs", "imps", "other"];

export function PaymentDialog({ open, onClose, presetParty, proformaId = "", onSaved }) {
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState(presetParty || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [auto, setAuto] = useState(true);
  const [openInvs, setOpenInvs] = useState([]);
  const [allocs, setAllocs] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPartyId(presetParty || ""); setAmount(""); setReference(""); setNotes(""); setAllocs({}); setAuto(true);
    api.get("/parties?limit=200").then((r) => setParties(r.data.items.filter((p) => !p.archived))).catch(() => {});
  }, [open, presetParty]);

  useEffect(() => {
    if (!partyId) { setOpenInvs([]); return; }
    api.get("/receivables").then((r) => setOpenInvs((r.data.items || []).filter((i) => i.party_id === partyId))).catch(() => setOpenInvs([]));
  }, [partyId]);

  const allocated = useMemo(() => Object.values(allocs).reduce((s, a) => s + (+a.amount || 0), 0), [allocs]);
  const diff = (+amount || 0) - allocated;

  const doAuto = () => {
    let remaining = +amount || 0;
    const next = {};
    for (const inv of openInvs) {
      if (remaining <= 0) break;
      const amt = Math.min(remaining, inv.balance);
      next[inv.id] = { invoice_id: inv.id, amount: amt };
      remaining -= amt;
    }
    setAllocs(next);
    toast.info("Allocated to oldest invoices first");
  };

  const save = async () => {
    if (!partyId) { toast.error("Select a customer"); return; }
    if (!(+amount > 0)) { toast.error("Enter a valid amount"); return; }
    const allocList = Object.values(allocs).filter((a) => +a.amount > 0).map((a) => ({ invoice_id: a.invoice_id, amount: +a.amount }));
    setBusy(true);
    try {
      const { data } = await api.post("/payments", {
        party_id: partyId, date, amount: +amount, mode, reference, notes,
        allocations: allocList, auto_allocate: false, proforma_id: proformaId || null,
      });
      toast.success(`Payment ${data.payment_no} recorded` +
        (data.unallocated > 0 ? ` · ${inr(data.unallocated)} kept as advance` : ""));
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="payment-dialog">
        <DialogHeader><DialogTitle className="font-display">Record Payment In</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Customer *</Label>
            <Select value={partyId} onValueChange={setPartyId}>
              <SelectTrigger data-testid="payment-party" className="mt-1.5"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Amount (₹) *</Label><Input data-testid="payment-amount" type="number" min="0" className="mt-1.5" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          <div><Label>Date</Label><Input data-testid="payment-date" type="date" className="mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><Label>Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="payment-mode" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Reference No</Label><Input data-testid="payment-reference" className="mt-1.5" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UPI / cheque / txn ref" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} className="mt-1.5" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>

        {partyId && (
          <div className="mt-2 rounded-lg border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/60 px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Allocate to open invoices</p>
              <Button size="sm" variant="ghost" data-testid="auto-allocate-btn" onClick={doAuto} disabled={!amount || openInvs.length === 0}>
                <Wand2 className="mr-1 h-3.5 w-3.5" /> Auto Allocate (oldest first)
              </Button>
            </div>
            {openInvs.length === 0 ? (
              <p className="p-4 text-sm text-slate-400">No open invoices — the full amount becomes customer advance.</p>
            ) : (
              <div className="max-h-56 overflow-y-auto" data-testid="allocation-list">
                {openInvs.map((inv) => {
                  const a = allocs[inv.id];
                  return (
                    <div key={inv.id} className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 px-3 py-2 text-sm">
                      <Checkbox data-testid={`alloc-check-${inv.invoice_no}`} checked={!!a}
                        onCheckedChange={(c) => setAllocs((p) => {
                          const n = { ...p };
                          if (c) n[inv.id] = { invoice_id: inv.id, amount: inv.balance }; else delete n[inv.id];
                          return n;
                        })} />
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-xs font-semibold truncate">{inv.invoice_no}</p>
                        <p className="text-xs text-slate-500">{fmtDate(inv.date)} · due {fmtDate(inv.due_date)}</p>
                      </div>
                      <span className="text-xs text-slate-500">bal <Money value={inv.balance} /></span>
                      {a && <Input data-testid={`alloc-amt-${inv.invoice_no}`} type="number" min="0" max={inv.balance}
                        className="h-8 w-28 font-mono text-xs" value={a.amount}
                        onChange={(e) => setAllocs((p) => ({ ...p, [inv.id]: { invoice_id: inv.id, amount: Math.min(+e.target.value || 0, inv.balance) } }))} />}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 px-3 py-2.5 text-xs font-medium bg-slate-50 dark:bg-slate-800/60" data-testid="allocation-summary">
              <span>Payment: <b className="font-mono">{inr(+amount || 0)}</b></span>
              <span>Allocated: <b className="font-mono text-emerald-600">{inr(allocated)}</b></span>
              <span className={diff < 0 ? "text-red-600" : diff > 0 ? "text-amber-600" : "text-slate-500"}>
                {diff < 0 ? "Over-allocated" : diff > 0 ? `Advance: ${inr(diff)}` : "Fully allocated"}
              </span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="payment-save-btn" onClick={save} disabled={busy || diff < 0}
            className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Record Payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LedgerTab() {
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [ledger, setLedger] = useState(null);
  const { activeId } = useCompany();
  const nav = useNavigate();

  useEffect(() => { api.get("/parties?limit=200").then((r) => setParties(r.data.items)).catch(() => {}); }, [activeId]);
  useEffect(() => {
    if (!partyId) { setLedger(null); return; }
    setLedger(null);
    api.get(`/ledger/${partyId}`).then((r) => setLedger(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [partyId]);

  return (
    <div>
      <Select value={partyId} onValueChange={setPartyId}>
        <SelectTrigger data-testid="ledger-party" className="w-72 mb-4 bg-white dark:bg-slate-900"><SelectValue placeholder="Select a party to view ledger" /></SelectTrigger>
        <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}</SelectContent>
      </Select>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
        {!partyId ? <p className="py-12 text-center text-sm text-slate-400">Choose a party above</p>
          : !ledger ? <TableSkeleton /> : (
            <>
              <div className="flex flex-wrap gap-4 border-b border-slate-100 dark:border-slate-800 p-4 text-xs" data-testid="ledger-summary">
                <span>Debit: <Money value={ledger.summary.total_debit} className="font-semibold" /></span>
                <span>Credit: <Money value={ledger.summary.total_credit} className="font-semibold" /></span>
                <span>Closing: <Money value={ledger.summary.closing_balance} className="font-bold text-red-600" /></span>
                <span>Advance: <Money value={ledger.summary.advance} className="font-semibold text-sky-600" /></span>
              </div>
              <table className="w-full text-sm" data-testid="ledger-table">
                <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="p-3">Date</th><th className="p-3">Type</th><th className="p-3">Document</th>
                  <th className="p-3 text-right">Debit</th><th className="p-3 text-right">Credit</th><th className="p-3 text-right">Balance</th></tr></thead>
                <tbody>
                  {ledger.entries.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-sm text-slate-400">No entries</td></tr>}
                  {ledger.entries.map((e, i) => (
                    <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="p-3">{fmtDate(e.date)}</td>
                      <td className="p-3 capitalize text-slate-500">{e.type.replace(/_/g, " ")}</td>
                      <td className="p-3 font-mono text-xs">
                        {e.type === "invoice" && e.doc_id
                          ? <button className="text-blue-600 hover:underline" onClick={() => nav(`/invoices/${e.doc_id}`)}>{e.doc_no}</button>
                          : e.doc_no}
                      </td>
                      <td className="p-3 text-right">{e.debit ? <Money value={e.debit} /> : "—"}</td>
                      <td className="p-3 text-right">{e.credit ? <Money value={e.credit} /> : "—"}</td>
                      <td className="p-3 text-right"><Money value={e.balance} className="font-semibold" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
      </div>
    </div>
  );
}

export default function Payments() {
  const { activeId } = useCompany();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "payments");
  const [payments, setPayments] = useState(null);
  const [receivables, setReceivables] = useState(null);
  const [unallocated, setUnallocated] = useState(null);
  const [dialog, setDialog] = useState(false);
  const presetParty = params.get("party") || "";
  const nav = useNavigate();

  const load = () => {
    api.get("/payments?limit=200").then((r) => setPayments(r.data.items)).catch(() => setPayments([]));
    api.get("/receivables").then((r) => setReceivables(r.data)).catch(() => setReceivables({ items: [] }));
    api.get("/unallocated").then((r) => setUnallocated(r.data)).catch(() => setUnallocated({ items: [] }));
  };
  useEffect(() => { if (activeId) { setPayments(null); setReceivables(null); setUnallocated(null); load(); } }, [activeId]); // eslint-disable-line
  useEffect(() => { if (params.get("new") === "1") setDialog(true); }, [params]);

  const cancelPayment = async (p) => {
    try {
      await api.post(`/payments/${p.id}/cancel`);
      toast.success(`Payment ${p.payment_no} cancelled and reversed`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="payments-page">
      <PageHeader title="Payments & Ledger" testId="payments-title" sub="Collections, allocation, receivables and party ledgers">
        <Button data-testid="new-payment-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Payment</Button>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="payments" data-testid="tab-payments-list"><IndianRupee className="mr-1.5 h-3.5 w-3.5" /> Payments</TabsTrigger>
          <TabsTrigger value="receivables" data-testid="tab-receivables">Receivables</TabsTrigger>
          <TabsTrigger value="unallocated" data-testid="tab-unallocated">Unallocated</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Party Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="payments">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {!payments ? <TableSkeleton /> : payments.length === 0 ? (
              <EmptyState testId="payments-empty" title="No payments yet" body="Record a customer payment and allocate it to invoices." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payments-table">
                  <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="p-3">Payment No</th><th className="p-3">Date</th><th className="p-3">Customer</th>
                    <th className="p-3">Mode</th><th className="p-3 text-right">Amount</th><th className="p-3 text-right">Allocated</th>
                    <th className="p-3 text-right">Advance</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} data-testid={`payment-row-${p.payment_no}`}
                        className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                        <td className="p-3 font-mono text-xs font-semibold">{p.payment_no} {p.is_demo && <span className="ml-1 rounded bg-slate-100 dark:bg-slate-800 px-1 text-[9px]">DEMO</span>}</td>
                        <td className="p-3">{fmtDate(p.date)}</td>
                        <td className="p-3 max-w-[180px] truncate">{p.party_name}</td>
                        <td className="p-3 capitalize text-slate-500">{p.mode?.replace(/_/g, " ")}</td>
                        <td className="p-3 text-right"><Money value={p.amount} className="font-semibold" /></td>
                        <td className="p-3 text-right"><Money value={p.allocated} className="text-emerald-600" /></td>
                        <td className="p-3 text-right"><Money value={p.unallocated} className={p.unallocated > 0 ? "text-amber-600 font-semibold" : "text-slate-400"} /></td>
                        <td className="p-3">
                          <Button size="sm" variant="ghost" data-testid={`payment-cancel-${p.payment_no}`}
                            className="text-red-500 h-7" onClick={() => cancelPayment(p)}>
                            <Ban className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="receivables">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {!receivables ? <TableSkeleton /> : receivables.items.length === 0 ? (
              <EmptyState testId="receivables-empty" title="Nothing to collect" body="All posted invoices are fully paid." />
            ) : (
              <>
                <div className="border-b border-slate-100 dark:border-slate-800 p-4 text-sm">
                  Total outstanding: <Money value={receivables.total_balance} className="font-bold text-red-600" />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="receivables-table">
                    <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      <th className="p-3">Invoice</th><th className="p-3">Customer</th><th className="p-3">Due Date</th>
                      <th className="p-3 text-right">Total</th><th className="p-3 text-right">Paid</th><th className="p-3 text-right">Balance</th>
                      <th className="p-3 text-right">Overdue</th><th className="p-3">Status</th></tr></thead>
                    <tbody>
                      {receivables.items.map((inv) => (
                        <tr key={inv.id} onClick={() => nav(`/invoices/${inv.id}`)}
                          className="cursor-pointer border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                          <td className="p-3 font-mono text-xs font-semibold">{inv.invoice_no}</td>
                          <td className="p-3 max-w-[180px] truncate">{inv.party_name}</td>
                          <td className="p-3">{fmtDate(inv.due_date)}</td>
                          <td className="p-3 text-right"><Money value={inv.total} /></td>
                          <td className="p-3 text-right"><Money value={inv.paid} /></td>
                          <td className="p-3 text-right"><Money value={inv.balance} className="font-semibold text-red-600" /></td>
                          <td className="p-3 text-right font-mono text-xs">{inv.days_overdue > 0 ? `${inv.days_overdue}d` : "—"}</td>
                          <td className="p-3"><StatusBadge status={inv.payment_status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="unallocated">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {!unallocated ? <TableSkeleton /> : unallocated.items.length === 0 ? (
              <EmptyState testId="unallocated-empty" title="No unallocated payments" body="Every rupee received is matched to invoices." />
            ) : (
              <>
                <div className="border-b border-slate-100 dark:border-slate-800 p-4 text-sm">
                  Total unallocated (customer advances): <Money value={unallocated.total_unallocated} className="font-bold text-amber-600" />
                </div>
                <table className="w-full text-sm" data-testid="unallocated-table">
                  <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="p-3">Payment No</th><th className="p-3">Date</th><th className="p-3">Customer</th>
                    <th className="p-3">Mode</th><th className="p-3 text-right">Amount</th><th className="p-3 text-right">Unallocated</th></tr></thead>
                  <tbody>
                    {unallocated.items.map((p) => (
                      <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="p-3 font-mono text-xs font-semibold">{p.payment_no}</td>
                        <td className="p-3">{fmtDate(p.date)}</td>
                        <td className="p-3">{p.party_name}</td>
                        <td className="p-3 capitalize text-slate-500">{p.mode?.replace(/_/g, " ")}</td>
                        <td className="p-3 text-right"><Money value={p.amount} /></td>
                        <td className="p-3 text-right"><Money value={p.unallocated} className="font-semibold text-amber-600" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ledger"><LedgerTab /></TabsContent>
      </Tabs>

      <PaymentDialog open={dialog} presetParty={presetParty}
        onClose={() => { setDialog(false); if (params.get("new")) setParams({}); }} onSaved={load} />
    </div>
  );
}
